import { type ReactElement, useState } from 'react';
import { type AuditEntry, type CaseType } from '@vello/api-client';
import { type OperatorRideTimeline, crossLinkTimeline, loadCaseAudit } from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { useCaseAuditText } from '../i18n/case-audit-text';
import { useScopeZone } from '../components/scope-zone';
import { formatMarketInstant as fmtZoned } from '../../src/index';
import { Badge, Button, Field, Notice, PageHeader, Panel, Select, Table, TextInput, FormRow, SectionTitle, Text, Row, Stack, EmptyCell, Ink } from '@vello/web-theme';

/**
 * Immutable audit (S114): read the append-only case audit trail and cross-link a case to its ride timeline.
 * The console displays the trail read-only — it never edits or deletes an entry.
 */
export function AuditScreen(): ReactElement {
  const { runtime } = useAdmin();
  const { t, tp } = useT();
  const { fail, v } = useVocab();
  const auditText = useCaseAuditText();
  const { zone, lang } = useScopeZone(); // D420: instants in the scope's market zone (A-18)
  const [type, setType] = useState<CaseType>('support');
  const [caseId, setCaseId] = useState('');
  const [entries, setEntries] = useState<readonly AuditEntry[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  const [rideId, setRideId] = useState('');
  const [timeline, setTimeline] = useState<OperatorRideTimeline | undefined>(undefined);
  const [timelineError, setTimelineError] = useState<string | undefined>(undefined);

  const onLoad = async (): Promise<void> => {
    setError(undefined);
    const res = await loadCaseAudit(runtime, type, caseId);
    if (res.ok) setEntries(res.value.entries);
    else { setEntries(undefined); setError(t('cases.error.audit', { code: fail(res.error) })); }
  };

  const onCrossLink = async (): Promise<void> => {
    setTimelineError(undefined);
    const res = await crossLinkTimeline(runtime, rideId);
    if (res.ok) setTimeline(res.value);
    else { setTimeline(undefined); setTimelineError(t('live.error.timeline', { code: fail(res.error) })); }
  };

  return (
    <>
      <PageHeader title={t('audit.title')} subtitle={t('audit.subtitle')} />

      <Panel><Stack gap={4}>
        <FormRow
          columns="160px minmax(0, 1fr)"
          action={
            <Button variant="primary" disabled={caseId === ''} onClick={() => void onLoad()}>{t('cases.loadAudit')}</Button>
          }
        >
          <Field label={t('audit.field.caseType')} htmlFor="type">
            <Select id="type" value={type} onChange={(e) => setType(e.target.value as CaseType)}>
              <option value="support">support</option>
              <option value="safety">safety</option>
              <option value="risk">risk</option>
            </Select>
          </Field>
          <Field label={t('cases.field.caseId')} htmlFor="case-id">
            <TextInput id="case-id" value={caseId} onChange={(e) => setCaseId(e.target.value)} />
          </Field>
        </FormRow>
        {error !== undefined && <Notice tone="danger">{error}</Notice>}
        {entries !== undefined && (
          <div>
            <Table
              columns={[
                { key: 'at', header: t('cases.col.when'), render: (e: AuditEntry) => <span className="tnum">{fmtZoned(e.at, zone, lang, 'datetime-seconds')}</span> },
                { key: 'actor', header: t('cases.col.actor'), render: (e: AuditEntry) => auditText.actor(e) },
                { key: 'action', header: t('cases.col.action'), render: (e: AuditEntry) => auditText.action(e) },
                { key: 'reason', header: t('cases.col.reason'), render: (e: AuditEntry) => auditText.reason(e) },
              ]}
              rows={entries}
              rowKey={(e) => `${e.at}-${e.action}-${e.actorId}`}
              empty={<EmptyCell>{t('cases.empty.audit')}</EmptyCell>}
            />
            <Text as="p" role="caption" tone="tertiary">{t('audit.readOnly')}</Text>
          </div>
        )}
      </Stack></Panel>

      <Panel>
        <SectionTitle>{t('audit.crossLink')}</SectionTitle>
        <FormRow
          action={
            <Button variant="secondary" disabled={rideId === ''} onClick={() => void onCrossLink()}>{t('audit.openTimeline')}</Button>
          }
        >
          <Field label={t('live.field.rideId')} htmlFor="xlink-ride">
            <TextInput id="xlink-ride" value={rideId} onChange={(e) => setRideId(e.target.value)} />
          </Field>
        </FormRow>
        {timelineError !== undefined && <Notice tone="danger">{timelineError}</Notice>}
        {timeline !== undefined && (
          <Row gap={3} align="center">
            <Badge tone="neutral">{v('ride.status', timeline.status)}</Badge>
            <Ink tone="secondary" mono>{timeline.marketId}</Ink>
            <Ink tone="tertiary" mono>{tp('live.events', timeline.events.length)}</Ink>
          </Row>
        )}
      </Panel>
    </>
  );
}
